import {useState} from 'react'

interface PaginationProps {
  currentPage: number
  totalPages: number
  setCurrentPage: (page: number) => void
}

export default function Pagination({currentPage, totalPages, setCurrentPage}: PaginationProps) {
  const [startPage, setStartPage] = useState(1)
  const [endPage, setEndPage] = useState(5)

  const handlePageClick = (page: number) => {
    if (page < 1 || page > totalPages) {
      return
    }
    setCurrentPage(page)
    if (page < startPage) {
      setStartPage(page)
      setEndPage(page + 4)
    } else if (page > endPage) {
      setEndPage(page)
      setStartPage(page - 4)
    }
  }

  const renderPageNumbers = () => {
    const pageNumbers: React.ReactNode[] = []
    const limiter = () => {
      if (currentPage <= 2) {
        return currentPage + 3
      } else return currentPage + 2
    }
    for (let i = currentPage - (totalPages - currentPage < 2 ? 3 : 2); i <= limiter(); i++) {
      i = i < 1 ? 1 : i
      if (i > totalPages) {
        break
      }

      pageNumbers.push(
        <li key={i} className={`page-item ${i === currentPage ? 'active' : ''}`}>
          <button type='button' className='page-link' onClick={() => handlePageClick(i)}>
            {i}
          </button>
        </li>
      )
    }

    return pageNumbers
  }

  const renderPreviousButton = () => {
    if (currentPage === 1) {
      return (
        <li className='page-item previous disabled'>
          <a href='#prev' className='page-link'>
            <i className='previous'></i>
          </a>
        </li>
      )
    }

    return (
      <li className='page-item previous'>
        <a href='#pprev' className='page-link' onClick={() => handlePageClick(currentPage - 1)}>
          <i className='previous'></i>
        </a>
      </li>
    )
  }

  const renderNextButton = () => {
    if (currentPage === totalPages) {
      return (
        <li className='page-item next disabled'>
          <a href='#next' className='page-link'>
            <i className='next'></i>
          </a>
        </li>
      )
    }

    return (
      <li className='page-item next'>
        <a href='#next' className='page-link' onClick={() => handlePageClick(currentPage + 1)}>
          <i className='next'></i>
        </a>
      </li>
    )
  }

  const renderFirstPageNumbers = () => {
    const pageNumbers: React.ReactNode[] = []

    for (let i = 1; i <= 5 && i <= totalPages; i++) {
      pageNumbers.push(
        <li key={i} className={`page-item ${i === currentPage ? 'active' : ''}`}>
          <button type='button' className='page-link' onClick={() => handlePageClick(i)}>
            {i}
          </button>
        </li>
      )
    }

    return pageNumbers
  }

  const renderLastPageNumbers = () => {
    const pageNumbers: React.ReactNode[] = []

    for (let i = totalPages - 4; i <= totalPages; i++) {
      if (i < 1) {
        continue
      }

      pageNumbers.push(
        <li key={i} className={`page-item ${i === currentPage ? 'active' : ''}`}>
          <button type='button' className='page-link' onClick={() => handlePageClick(i)}>
            {i}
          </button>
        </li>
      )
    }

    return pageNumbers
  }

  const renderPageNumbersBasedOnCurrentPage = () => {
    if (currentPage === 1) {
      return renderFirstPageNumbers()
    } else if (currentPage === totalPages) {
      return renderLastPageNumbers()
    } else {
      return renderPageNumbers()
    }
  }

  const renderLastPage = () => {
    if (currentPage > totalPages - 5) {
      return null
    }

    return (
      <>
        <li className='page-item'>
          <span className='page-link'>...</span>
        </li>
        <li className='page-item'>
          <button type='button' className='page-link' onClick={() => handlePageClick(totalPages)}>
            {totalPages}
          </button>
        </li>
      </>
    )
  }

  const renderFirstPage = () => {
    if (currentPage < 5) {
      return null
    }

    return (
      <>
        <li className='page-item'>
          <button type='button' className='page-link' onClick={() => handlePageClick(1)}>
            1
          </button>
        </li>
        <li className='page-item'>
          <span className='page-link'>...</span>
        </li>
      </>
    )
  }

  return (
    <>
      <ul className='pagination pagination-circle'>
        {renderPreviousButton()}
        {renderFirstPage()}
        {renderPageNumbersBasedOnCurrentPage()}
        {renderLastPage()}
        {renderNextButton()}
      </ul>
    </>
  )
}
